import { useCallback, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import './compareSlider.css'

/**
 * Before and after, on top of each other, with a line between them
 * -------------------------------------------------------------
 * The upscaled picture fills the frame and the original is laid over it up to
 * the line. Drag the line all the way left and all of it is the upscaled
 * picture; all the way right and all of it is the original (Huanrui,
 * 2026-10-05). Both are drawn at the same size in the same box, so whatever is
 * under the line is the same spot in both.
 *
 * The arrow keys move it too, a tenth at a time.
 */

interface CompareSliderProps {
  /** The original. */
  before: string
  /** The upscaled picture. */
  after: string
  /** Width over height, so the box is the picture's shape before either has loaded. */
  ratio: number
  beforeLabel?: string
  afterLabel?: string
}

export function CompareSlider({ before, after, ratio, beforeLabel = 'Original', afterLabel = 'Upscaled' }: CompareSliderProps) {
  const box = useRef<HTMLDivElement | null>(null)
  /** Where the line is, from 0 (left) to 100 (right). */
  const [at, setAt] = useState(50)
  const dragging = useRef(false)

  const moveTo = useCallback((clientX: number) => {
    const rect = box.current?.getBoundingClientRect()
    if (!rect || rect.width <= 0) return
    setAt(Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)))
  }, [])

  const down = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    dragging.current = true
    event.currentTarget.setPointerCapture(event.pointerId)
    moveTo(event.clientX)
  }
  const move = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return
    // Let go outside the window, or the button released some other way.
    if (event.buttons === 0) {
      dragging.current = false
      return
    }
    moveTo(event.clientX)
  }
  const up = (event: PointerEvent<HTMLDivElement>) => {
    dragging.current = false
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }
  const key = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') setAt((was) => Math.max(0, was - 10))
    else if (event.key === 'ArrowRight') setAt((was) => Math.min(100, was + 10))
    else if (event.key === 'Home') setAt(0)
    else if (event.key === 'End') setAt(100)
    else return
    event.preventDefault()
  }

  const shape = Number.isFinite(ratio) && ratio > 0 ? ratio : 1

  return (
    <div
      ref={box}
      className="wwcmp"
      style={{ aspectRatio: String(shape), ['--wwcmp-ratio' as string]: String(shape) }}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      onLostPointerCapture={() => {
        dragging.current = false
      }}
      onKeyDown={key}
      role="slider"
      tabIndex={0}
      aria-label="Compare the original with the upscaled picture"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(at)}
      aria-valuetext={at <= 0 ? afterLabel : at >= 100 ? beforeLabel : `${Math.round(at)}% ${beforeLabel.toLowerCase()}`}
    >
      <img className="wwcmp__img" src={after} alt={afterLabel} draggable={false} />
      <img
        className="wwcmp__img wwcmp__img--before"
        src={before}
        alt={beforeLabel}
        draggable={false}
        style={{ clipPath: `inset(0 ${100 - at}% 0 0)` }}
      />
      <span className="wwcmp__tag wwcmp__tag--before" style={{ opacity: at > 8 ? 1 : 0 }}>
        {beforeLabel}
      </span>
      <span className="wwcmp__tag wwcmp__tag--after" style={{ opacity: at < 92 ? 1 : 0 }}>
        {afterLabel}
      </span>
      <span className="wwcmp__line" style={{ left: `${at}%` }} aria-hidden="true">
        <span className="wwcmp__grip">
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <path d="M6 3 1 8l5 5M10 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </span>
    </div>
  )
}
